import type { PoolFocus } from '../app/navigation';
import type { V2Evidence } from '../domain/evidence';
import type { V2Area, V2ChecklistItem } from '../domain/inspection';
import type { EvidenceState } from './evidenceStore';
import type { FindingState } from './findingStore';
import type { InspectionState } from './inspectionStore';

// The PHOTO POOL (Owner task book 2026-09-04 §9): every photo of the inspection — bulk-imported
// pool photos and photos taken in areas / items / findings alike — with the links it has. A
// photo is LINKED when at least one item or finding references it; linking never moves or copies
// the photo, and a linked photo stays in the pool. Pure derivation over the three stores.
// 2026-10-27: a photo the AI PLACED on a row (「AI 放的」, V2ChecklistItem.evidenceSuggestions) is a link like
// any other — it counts as linked in the filters, the counts and the picker, and it is in the report — and
// only carries the mark so the pool card can say the AI put it there. (2026-10-18 … 10-26 it was a pending
// proposal that counted as unlinked.)
export interface PoolLink {
  kind: 'item' | 'finding';
  id: string;
  // Item title / finding title (a finding title may be empty — the UI shows its fallback).
  title: string;
  areaId: string;
  // true = the AI placed this link and the inspector has not touched it yet; absent / false = the inspector's.
  pending?: boolean;
}

export interface PoolEntry {
  evidence: V2Evidence;
  links: PoolLink[];
  // The area the photo was captured in (null for pool imports).
  captureArea: V2Area | null;
}

export type PoolFilter = 'all' | 'unlinked' | 'linked';

export const POOL_FILTER_ORDER: readonly PoolFilter[] = Object.freeze(['all', 'unlinked', 'linked']);

// The links the inspector made or confirmed (no AI mark).
export function confirmedLinks(entry: Pick<PoolEntry, 'links'>): PoolLink[] {
  return entry.links.filter((l) => l.pending !== true);
}

// The links the AI placed that the inspector has not touched (「AI 放的」).
export function pendingLinks(entry: Pick<PoolEntry, 'links'>): PoolLink[] {
  return entry.links.filter((l) => l.pending === true);
}

// Linked = any link, the inspector's or the AI's (2026-10-27).
export function entryIsLinked(entry: Pick<PoolEntry, 'links'>): boolean {
  return entry.links.length > 0;
}

export function buildPhotoPool(inspectionId: string, evidence: EvidenceState, inspection: InspectionState, findings: FindingState): PoolEntry[] {
  const areasById = new Map(inspection.areas.filter((a) => a.inspectionId === inspectionId).map((a) => [a.id, a] as const));
  const items = inspection.items.filter((i) => i.inspectionId === inspectionId);
  const findingList = findings.findings.filter((f) => f.inspectionId === inspectionId);
  return evidence.evidence
    .filter((e) => e.inspectionId === inspectionId)
    .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id))
    .map((ev) => {
      const links: PoolLink[] = [];
      for (const item of items) {
        if (item.evidenceIds.includes(ev.id)) links.push({ kind: 'item', id: item.id, title: item.title, areaId: item.areaId, ...(item.evidenceSuggestions.includes(ev.id) ? { pending: true } : {}) });
      }
      for (const finding of findingList) if (finding.evidenceIds.includes(ev.id)) links.push({ kind: 'finding', id: finding.id, title: finding.title, areaId: finding.areaId });
      return { evidence: ev, links, captureArea: ev.areaId ? (areasById.get(ev.areaId) ?? null) : null };
    });
}

export function filterPool(entries: readonly PoolEntry[], filter: PoolFilter): PoolEntry[] {
  switch (filter) {
    case 'all':
      return [...entries];
    case 'unlinked':
      return entries.filter((e) => !entryIsLinked(e));
    case 'linked':
      return entries.filter(entryIsLinked);
  }
}

export interface PoolCounts {
  all: number;
  unlinked: number;
  linked: number;
}

export function poolCounts(entries: readonly PoolEntry[]): PoolCounts {
  const linked = entries.filter(entryIsLinked).length;
  return { all: entries.length, linked, unlinked: entries.length - linked };
}

// Pool entries that are NOT yet linked to the given target (what a picker offers).
export function poolCandidatesFor(entries: readonly PoolEntry[], target: { itemId: string | null; findingId: string | null; linkedIds?: readonly string[] }): PoolEntry[] {
  const already = new Set(target.linkedIds ?? []);
  return entries.filter((e) => {
    if (already.has(e.evidence.id)) return false;
    return !e.links.some((l) => (l.kind === 'item' && l.id === target.itemId) || (l.kind === 'finding' && l.id === target.findingId));
  });
}

// ---------- area-first index (Round 2 §S) ----------
//
// A photo belongs to an area when it was CAPTURED there or when any of its links (item / finding,
// confirmed or AI-proposed) lives there — association metadata drives the categorization; the file is
// never moved or copied, so one photo may count under several areas. Unclassified = no capture area and
// no link of either kind.
export function entryBelongsToArea(entry: PoolEntry, areaId: string): boolean {
  return entry.evidence.areaId === areaId || entry.links.some((l) => l.areaId === areaId);
}

export function entryIsUnclassified(entry: PoolEntry): boolean {
  return entry.evidence.areaId === null && entry.links.length === 0;
}

export interface PoolAreaRow {
  area: V2Area;
  count: number;
}

export interface PoolAreaIndex {
  rows: PoolAreaRow[];
  unclassified: number;
  total: number;
}

export function poolAreaIndex(entries: readonly PoolEntry[], areas: readonly V2Area[]): PoolAreaIndex {
  return {
    rows: areas.map((area) => ({ area, count: entries.filter((e) => entryBelongsToArea(e, area.id)).length })),
    unclassified: entries.filter(entryIsUnclassified).length,
    total: entries.length
  };
}

export function entriesForFocus(entries: readonly PoolEntry[], focus: PoolFocus): PoolEntry[] {
  switch (focus.kind) {
    case 'all':
      return [...entries];
    case 'unclassified':
      return entries.filter(entryIsUnclassified);
    case 'area':
      return entries.filter((e) => entryBelongsToArea(e, focus.areaId));
  }
}

export function itemsLinkedTo(entry: PoolEntry, items: readonly V2ChecklistItem[]): V2ChecklistItem[] {
  const ids = new Set(entry.links.filter((l) => l.kind === 'item').map((l) => l.id));
  return items.filter((i) => ids.has(i.id));
}

// ---------- the photos of an area's AI scan (Owner device test 2026-10-05) ----------
//
// What the area page's AI card shows and analyzes: the photos taken in the area at area level, AND the
// pool imports filed into the area — linked to one of its rows or findings, by the inspector or by
// 「AI 分析照片池」 (2026-10-18: a pool-sort placement is a PROPOSAL on the row, and it still places the
// photo in the area here). Without the second half the photos the pool analysis sorted into the kitchen
// sat on the kitchen's rows while the kitchen's own AI card said 0 photos. A photo taken AT a row (capture
// item set) stays that row's, as before. Nothing is moved or copied: this is a view over the links.
export function areaScanPhotos(evidence: EvidenceState, inspection: InspectionState, findings: FindingState, areaId: string): V2Evidence[] {
  const filed = new Set<string>();
  for (const item of inspection.items) {
    if (item.areaId !== areaId) continue;
    for (const id of item.evidenceIds) filed.add(id);
    for (const id of item.evidenceSuggestions) filed.add(id);
  }
  for (const finding of findings.findings) if (finding.areaId === areaId) for (const id of finding.evidenceIds) filed.add(id);
  return evidence.evidence.filter((e) => (e.areaId === areaId && e.itemId === null) || (e.areaId === null && filed.has(e.id)));
}
